/**
 * GraphCanvas — ReactFlow canvas for the MAPPING CANVAS (field-level detail).
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │  THIS IS THE MAPPING CANVAS.                                        │
 * │  It renders field-level mapping nodes and edges.                    │
 * │                                                                     │
 * │  NOT the pipeline/workspace canvas. That's DAGCanvas.tsx in         │
 * │  ui/src/components/workspace/.                                      │
 * │                                                                     │
 * │  See docs/CANVAS_ARCHITECTURE.md for the full distinction.          │
 * └──────────────────────────────────────────────────────────────────────┘
 */
import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlowProvider,
  SelectionMode,
  useReactFlow,
  useEdgesState,
  useNodesState,
} from 'reactflow';
import type { Connection, NodeChange, OnSelectionChangeParams } from 'reactflow';
import 'reactflow/dist/style.css';
import type { CanvasEdge, CanvasGraph, CanvasNode, CanvasSelection } from './types';
import {
  AggregationNode,
  IntermediateFieldNode,
  JoinNode,
  LinkBridgeNode,
  SourceFieldNode,
  SourceOriginGroupNode,
  TableGroupNode,
  TableNode,
  TargetFieldNode,
  TransformationNode,
  WindowFunctionNode,
} from './nodes';
import { BundleEdge, ConfidenceEdge } from './edges';
import { CANVAS_ROW_SPACING } from './layoutConstants';

type GraphCanvasProps = {
  graph: CanvasGraph;
  onSelectionChange?: (selection: CanvasSelection | null) => void;
  onNodeClick?: (event: React.MouseEvent, node: CanvasNode) => void;
  onConnect?: (connection: Connection) => void;
  onPaneClick?: () => void;
  loading?: boolean;
  overlay?: ReactNode;
  showGrid?: boolean;
  revision?: number | string;
  focusModeEnabled?: boolean;
  focusNodeId?: string | null;
  fitViewTrigger?: number;
  highlightNodeIds?: Set<string>;
  /** Phase 4.8: Right-click context menu handler for nodes. */
  onNodeContextMenu?: (event: React.MouseEvent, node: CanvasNode) => void;
};

const nodeTypes = {
  sourceField: SourceFieldNode,
  targetField: TargetFieldNode,
  transformation: TransformationNode,
  aggregation: AggregationNode,
  intermediateField: IntermediateFieldNode,
  linkBridge: LinkBridgeNode,
  tableGroup: TableGroupNode,
  sourceOriginGroup: SourceOriginGroupNode,
  joinNode: JoinNode,
  tableNode: TableNode,
  windowFunction: WindowFunctionNode,
};

const edgeTypes = {
  confidence: ConfidenceEdge,
  bundle: BundleEdge,
};

const GraphCanvasInner: React.FC<GraphCanvasProps> = ({ graph, onSelectionChange, onNodeClick, onConnect, onPaneClick, loading, overlay, showGrid = true, revision, focusModeEnabled = false, focusNodeId = null, fitViewTrigger = 0, highlightNodeIds, onNodeContextMenu }) => {
  const [nodes, setNodes, onNodesChangeBase] = useNodesState(graph.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(graph.edges);
  const reactFlowInstance = useReactFlow();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [initialViewportApplied, setInitialViewportApplied] = useState(false);

  // ─── Drag-aware change handler ───
  // Track whether a drag is in progress so we can freeze expensive
  // recomputations (adjacency, styledNodes, styledEdges) during drag.
  const isDraggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);

  // Keep latest nodes/edges in refs so the onNodesChange callback can
  // snapshot them without re-creating the callback on every frame.
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  nodesRef.current = nodes;
  edgesRef.current = edges;

  // Snapshot of nodes/edges identity taken before the drag starts.
  // Only used for adjacency/BFS computations (topology doesn't change during drag).
  const preDragNodesRef = useRef(nodes);
  const preDragEdgesRef = useRef(edges);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Detect drag start/end from the change stream
      const hasDragStart = changes.some(c => c.type === 'position' && c.dragging === true);
      const hasDragEnd = changes.some(c => c.type === 'position' && c.dragging === false);

      if (hasDragStart && !isDraggingRef.current) {
        isDraggingRef.current = true;
        // Snapshot current nodes/edges before drag mutates positions
        preDragNodesRef.current = nodesRef.current;
        preDragEdgesRef.current = edgesRef.current;
        setIsDragging(true);
      }
      if (hasDragEnd && isDraggingRef.current) {
        isDraggingRef.current = false;
        setIsDragging(false);
      }

      // Always forward changes so ReactFlow updates positions visually
      onNodesChangeBase(changes);
    },
    [onNodesChangeBase],
  );

  // Use pre-drag snapshots for adjacency/BFS computations during drag
  // (topology doesn't change while dragging — only positions do).
  const nodesForComputation = isDragging ? preDragNodesRef.current : nodes;
  const edgesForComputation = isDragging ? preDragEdgesRef.current : edges;

  // Debounced hover state: avoid triggering expensive BFS + restyle on every pointermove
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingHoverRef = useRef<string | null>(null);
  const setHoveredNodeIdDebounced = useCallback((nodeId: string | null) => {
    pendingHoverRef.current = nodeId;
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    if (nodeId === null) {
      // Delay clearing to avoid flash-off during rapid hover transitions
      hoverTimerRef.current = setTimeout(() => {
        setHoveredNodeId(null);
      }, 100);
    } else {
      hoverTimerRef.current = setTimeout(() => {
        setHoveredNodeId(nodeId);
      }, 80);
    }
  }, []);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  // Detect major graph structure changes (e.g. view mode switch) and reset viewport
  const prevNodeSignatureRef = useRef<string>('');
  useEffect(() => {
    // Build a lightweight signature from node types to detect view mode switches
    const sig = graph.nodes.slice(0, 5).map(n => n.type).join(',');
    if (prevNodeSignatureRef.current && sig !== prevNodeSignatureRef.current) {
      setInitialViewportApplied(false);
    }
    prevNodeSignatureRef.current = sig;
  }, [graph.nodes]);

  // Reset viewport when parent requests a fit (e.g. table focus toggle in overview panel)
  const prevFitViewTriggerRef = useRef(fitViewTrigger);
  useEffect(() => {
    if (fitViewTrigger !== prevFitViewTriggerRef.current) {
      setInitialViewportApplied(false);
      prevFitViewTriggerRef.current = fitViewTrigger;
    }
  }, [fitViewTrigger]);

  // Sync graph data into ReactFlow state.
  // IMPORTANT: Preserve user-dragged positions when only non-structural data
  // changes (e.g. sample preview data, expand/collapse state).  We detect this
  // by checking if the node set is the same (same ids) and only update data/style
  // while keeping the user-modified positions.
  useEffect(() => {
    setNodes((currentNodes) => {
      // If there are no current nodes, accept the graph as-is (initial load)
      if (currentNodes.length === 0) return graph.nodes;

      // Build lookup of current positions by node id
      const currentPosById = new Map<string, { x: number; y: number }>();
      currentNodes.forEach(n => {
        if (n.position) currentPosById.set(n.id, n.position);
      });

      // Check if it's a structural change (different node set) vs data-only change
      const currentIds = new Set(currentNodes.map(n => n.id));
      const graphIds = new Set(graph.nodes.map(n => n.id));
      const isStructuralChange =
        currentIds.size !== graphIds.size ||
        graph.nodes.some(n => !currentIds.has(n.id));

      if (isStructuralChange) {
        // Full replacement — new graph structure
        return graph.nodes;
      }

      // Data-only update: merge graph data/style but keep user-dragged positions
      return graph.nodes.map(graphNode => {
        const currentPos = currentPosById.get(graphNode.id);
        if (currentPos) {
          return { ...graphNode, position: currentPos };
        }
        return graphNode;
      });
    });
  }, [graph.nodes, revision, setNodes]);

  useEffect(() => {
    setEdges(graph.edges);
  }, [graph.edges, revision, setEdges]);

  // Deferred fitView after nodes/edges are set in ReactFlow store.
  // When the graph structure changes drastically (e.g. field↔table view switch),
  // ReactFlow needs extra time to lay out the new nodes before fitView can
  // compute correct viewport bounds.  We use a two-stage approach:
  //   1. requestAnimationFrame to wait for React commit
  //   2. setTimeout to let ReactFlow's internal layout settle
  useEffect(() => {
    if (!initialViewportApplied && graph.nodes.length > 0 && reactFlowInstance) {
      const raf = requestAnimationFrame(() => {
        // First attempt after a short delay
        const timer1 = setTimeout(() => {
          reactFlowInstance.fitView({ padding: 0.15, duration: 300 });
          setInitialViewportApplied(true);
          // Second attempt after ReactFlow has fully settled — catches cases
          // where the first fitView calculated bounds from stale positions
          const timer2 = setTimeout(() => {
            reactFlowInstance.fitView({ padding: 0.15, duration: 250 });
          }, 300);
          return () => clearTimeout(timer2);
        }, 80);
        return () => clearTimeout(timer1);
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [initialViewportApplied, graph.nodes, reactFlowInstance]);

  // Scroll canvas to a specific node when focusNodeId changes.
  // NOTE: depends only on focusNodeId — NOT on nodes — so dragging nodes
  // doesn't re-trigger the centering and appear to "reset" positions.
  const prevFocusNodeIdRef = useRef(focusNodeId);
  useEffect(() => {
    if (!focusNodeId || !reactFlowInstance) return;
    // Only re-center when focusNodeId actually changes (not on every nodes update)
    if (focusNodeId === prevFocusNodeIdRef.current) return;
    prevFocusNodeIdRef.current = focusNodeId;
    const node = reactFlowInstance.getNode(focusNodeId);
    if (!node) return;
    // Use positionAbsolute which accounts for parent node offsets (child nodes have relative positions)
    const absPos = node.positionAbsolute ?? node.position;
    const x = absPos?.x ?? 0;
    const y = absPos?.y ?? 0;
    const width = typeof node.style?.width === 'number' ? node.style.width : 260;
    const height = typeof node.style?.height === 'number' ? node.style.height : 40;
    reactFlowInstance.setCenter(x + width / 2, y + height / 2, { zoom: 1.2, duration: 400 });
  }, [focusNodeId, reactFlowInstance]);

  const graphBounds = useMemo(() => {
    if (!graph.nodes.length) {
      return null;
    }
    return graph.nodes.reduce(
      (acc, node) => {
        const width = typeof node.style?.width === 'number' ? node.style.width : 260;
        const height = typeof node.style?.height === 'number' ? node.style.height : CANVAS_ROW_SPACING * 1.5;
        const minX = Math.min(acc.minX, node.position?.x ?? 0);
        const minY = Math.min(acc.minY, node.position?.y ?? 0);
        const maxX = Math.max(acc.maxX, (node.position?.x ?? 0) + width);
        const maxY = Math.max(acc.maxY, (node.position?.y ?? 0) + height);
        return { minX, minY, maxX, maxY };
      },
      { minX: Number.POSITIVE_INFINITY, minY: Number.POSITIVE_INFINITY, maxX: Number.NEGATIVE_INFINITY, maxY: Number.NEGATIVE_INFINITY }
    );
  }, [graph.nodes]);

  useLayoutEffect(() => {
    if (!graphBounds || !containerRef.current) {
      return;
    }
    // Skip the synchronous fitView when viewport hasn't been applied yet —
    // the deferred useEffect above handles the initial fit with proper timing.
    // Calling fitView here too early (before ReactFlow processes nodes) causes
    // the viewport to jump off-screen (e.g. y=-1122px on table view switch).
    if (!initialViewportApplied) {
      return;
    }

    const containerHeight = containerRef.current.clientHeight || 1;
    const containerWidth = containerRef.current.clientWidth || 1;
    const graphHeight = graphBounds.maxY - graphBounds.minY;
    const graphWidth = graphBounds.maxX - graphBounds.minX;
    const needsScrollZoom = graphHeight > containerHeight * 1.35 || graphWidth > containerWidth * 1.2;

    if (!needsScrollZoom) {
      reactFlowInstance.fitView({ padding: 0.18, duration: 300 });
    }
  }, [graphBounds, initialViewportApplied, reactFlowInstance]);

  // Pre-compute adjacency map and node lookup for fast BFS (avoids O(n) find per step).
  // Uses pre-drag snapshots during drag to avoid recomputing on every position tick.
  const { adjacency, nodeMap, parentMap, childrenMap } = useMemo(() => {
    const adj = new Map<string, string[]>();
    const nMap = new Map<string, typeof nodesForComputation[0]>();
    const pMap = new Map<string, string>();
    const cMap = new Map<string, string[]>();

    nodesForComputation.forEach(n => {
      nMap.set(n.id, n);
      if (n.parentNode) {
        pMap.set(n.id, n.parentNode);
        const siblings = cMap.get(n.parentNode) ?? [];
        siblings.push(n.id);
        cMap.set(n.parentNode, siblings);
      }
    });
    edgesForComputation.forEach(e => {
      const srcList = adj.get(e.source) ?? [];
      srcList.push(e.target);
      adj.set(e.source, srcList);
      const tgtList = adj.get(e.target) ?? [];
      tgtList.push(e.source);
      adj.set(e.target, tgtList);
    });
    return { adjacency: adj, nodeMap: nMap, parentMap: pMap, childrenMap: cMap };
  }, [nodesForComputation, edgesForComputation]);

  // Compute connected node set for the active (hovered/selected) node
  const connectedNodeIds = useMemo<Set<string> | null>(() => {
    const activeNodeId = hoveredNodeId ?? selectedNodeId;
    if (!activeNodeId) return null;

    const findConnected = (startId: string): Set<string> => {
      const connected = new Set<string>();
      const queue = [startId];
      while (queue.length > 0) {
        const currentId = queue.shift()!;
        if (connected.has(currentId)) continue;
        connected.add(currentId);
        const parent = parentMap.get(currentId);
        if (parent) connected.add(parent);
        const neighbors = adjacency.get(currentId) ?? [];
        for (const neighbor of neighbors) {
          if (!connected.has(neighbor)) queue.push(neighbor);
        }
      }
      return connected;
    };

    let result: Set<string>;
    const activeNode = nodeMap.get(activeNodeId);
    if (activeNode?.data?.kind === 'tableGroup') {
      result = new Set<string>([activeNodeId]);
      const children = childrenMap.get(activeNodeId) ?? [];
      children.forEach(childId => {
        result.add(childId);
        findConnected(childId).forEach(id => result.add(id));
      });
    } else {
      result = findConnected(activeNodeId);
    }
    // Also include all children of any connected group nodes
    result.forEach(id => {
      const children = childrenMap.get(id);
      if (children) children.forEach(cid => result.add(cid));
    });
    return result;
  }, [hoveredNodeId, selectedNodeId, adjacency, nodeMap, parentMap, childrenMap]);

  // Phase 1 (P0): Node styling — dim non-connected nodes when hovering/selecting,
  // highlight search matches, and apply focus mode blur.
  // During drag, pass through raw nodes (positions only) to avoid O(n) remap per frame.
  const styledNodes = useMemo(() => {
    // During active drag, skip expensive style computation — just use live nodes
    if (isDragging) return nodes;

    const hasHighlight = highlightNodeIds && highlightNodeIds.size > 0;
    const hasActive = connectedNodeIds !== null;

    if (!focusModeEnabled && !hasActive && !hasHighlight) {
      return nodes;
    }

    return nodes.map(node => {
      const isFocused = connectedNodeIds ? connectedNodeIds.has(node.id) : true;
      const isHighlighted = hasHighlight ? highlightNodeIds!.has(node.id) : false;
      // Skip creating new object if opacity would be 1 and no highlight
      const opacity = !isFocused ? 0.35 : (hasHighlight && !isHighlighted ? 0.5 : 1);
      if (opacity === 1 && !isHighlighted && !node.style?.opacity) {
        return node; // same reference = no re-render for this node
      }
      return {
        ...node,
        style: {
          ...node.style,
          opacity,
          filter: 'none',
          transition: 'opacity 0.2s, filter 0.2s',
          ...(isHighlighted ? {
            outline: '2px solid #f59e0b',
            outlineOffset: '2px',
            borderRadius: '8px',
          } : {}),
        },
      };
    });
  }, [nodes, isDragging, focusModeEnabled, connectedNodeIds, highlightNodeIds]);

  // Phase 1 (P0): Edge styling — edges are visible at moderate opacity by default;
  // hovering or selecting a node reveals edges in the connected subgraph at full opacity.
  // Optimized: returns same reference when nothing active, uses CSS transitions for smooth UX.
  const baseEdgeStyle = useMemo(() => ({ opacity: 0.45, transition: 'opacity 0.25s ease-in-out' }), []);
  const styledEdges = useMemo(() => {
    // During active drag, skip expensive style computation
    if (isDragging) return edges;

    // When nothing is active, apply default moderate opacity
    if (!connectedNodeIds) {
      return edges.map(edge => ({
        ...edge,
        style: { ...edge.style, ...baseEdgeStyle },
      }));
    }

    return edges.map(edge => {
      const isFocused = connectedNodeIds.has(edge.source) && connectedNodeIds.has(edge.target);
      return {
        ...edge,
        style: {
          ...edge.style,
          opacity: isFocused ? 1 : 0.12,
          transition: 'opacity 0.25s ease-in-out',
        },
      };
    });
  }, [edges, isDragging, connectedNodeIds, baseEdgeStyle]);

  // ─── Memoized callbacks for ReactFlow to avoid re-renders ───
  const minimapStyle = useMemo(() => ({
    backgroundColor: '#fff',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    bottom: 16,
    right: 16,
  }), []);

  const controlsStyle = useMemo(() => ({
    backgroundColor: '#fff',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    top: 16,
    right: 16,
    left: 'auto' as const,
    padding: 4,
    height: 'fit-content' as const,
    display: 'flex' as const,
    flexDirection: 'column' as const,
  }), []);

  const minimapNodeColor = useCallback((node: { type?: string }) => {
    switch (node.type) {
      case 'sourceField': return '#bfdbfe';
      case 'targetField': return '#bbf7d0';
      case 'transformation': return '#e9d5ff';
      case 'aggregation': return '#bae6fd';
      default: return '#e2e8f0';
    }
  }, []);

  const isValidConnectionCb = useCallback((connection: Connection) => {
    const srcId = connection.source ?? '';
    const tgtId = connection.target ?? '';
    return srcId.startsWith('sourceField:') && tgtId.startsWith('targetField:');
  }, []);

  const connectionLineStyleMemo = useMemo(
    () => ({ stroke: '#2563eb', strokeWidth: 2, strokeDasharray: '6 3' }),
    [],
  );

  const handleNodeMouseEnter = useCallback(
    (_: React.MouseEvent, node: { id: string }) => setHoveredNodeIdDebounced(node.id),
    [setHoveredNodeIdDebounced],
  );

  const handleNodeMouseLeave = useCallback(
    () => setHoveredNodeIdDebounced(null),
    [setHoveredNodeIdDebounced],
  );

  const handleNodeClick = useCallback(
    (event: React.MouseEvent, node: CanvasNode) => onNodeClick?.(event, node),
    [onNodeClick],
  );

  const handleNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: CanvasNode) => {
      event.preventDefault();
      onNodeContextMenu?.(event, node);
    },
    [onNodeContextMenu],
  );

  const handleSelectionChange = useCallback(
    (selection: OnSelectionChangeParams) => {
      const selectedNode = selection.nodes?.[0];
      if (selectedNode) {
        setSelectedNodeId(selectedNode.id);
        onSelectionChange?.({ kind: 'node', node: selectedNode as CanvasNode });
        return;
      }
      const selectedEdge = selection.edges?.[0];
      if (selectedEdge) {
        setSelectedNodeId(null);
        onSelectionChange?.({ kind: 'edge', edge: selectedEdge as CanvasEdge });
        return;
      }
      // Don't forward null from onSelectionChange — these fire spuriously
      // when the graph re-renders (e.g. auto-fetch preview, density change).
      // Intentional deselection is handled by onPaneClick below.
      setSelectedNodeId(null);
    },
    [onSelectionChange],
  );

  const handlePaneClick = useCallback(() => {
    onPaneClick?.();
  }, [onPaneClick]);

  return (
    <Box
      ref={containerRef}
      sx={{ position: 'relative', flex: 1, minHeight: 640, borderRadius: 2, overflow: 'hidden' }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(255,255,255,0.4)',
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {overlay && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            zIndex: 3,
            maxWidth: '28%',
            pointerEvents: 'auto',
          }}
        >
          {overlay}
        </Box>
      )}
      <ReactFlow
        nodes={styledNodes}
        edges={styledEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        panOnScroll
        zoomOnScroll
        zoomOnPinch
        zoomOnDoubleClick={false}
        nodesDraggable={true}
        nodesConnectable={true}
        onConnect={onConnect}
        isValidConnection={isValidConnectionCb}
        connectionLineStyle={connectionLineStyleMemo}
        selectionOnDrag={false}
        selectionMode={SelectionMode.Partial}
        onNodeMouseEnter={handleNodeMouseEnter}
        onNodeMouseLeave={handleNodeMouseLeave}
        onNodeClick={handleNodeClick}
        onNodeContextMenu={handleNodeContextMenu}
        onSelectionChange={handleSelectionChange}
        onPaneClick={handlePaneClick}
      >
        <MiniMap
          pannable
          zoomable
          style={minimapStyle}
          nodeColor={minimapNodeColor}
        />
        <Controls
          showInteractive={false}
          style={controlsStyle}
        />
        {showGrid && <Background variant={BackgroundVariant.Dots} color="#cbd5f5" gap={24} size={1.5} />}
      </ReactFlow>
    </Box>
  );
};

const GraphCanvas: React.FC<GraphCanvasProps> = (props) => (
  <ReactFlowProvider>
    <GraphCanvasInner {...props} />
  </ReactFlowProvider>
);

export default GraphCanvas;
